/**
 * Wandering trader and llama models: real vanilla geometry (ModelPart boxes,
 * pivots, UV offsets) over the real sheets in public/mc/entity (model px space,
 * y down, front -z, feet at y = 24).
 *  - wandering trader: VillagerModel (head + nose + hood layer, robe, folded
 *    arms, legs) on wandering_trader.png (64x64).
 *  - llama / trader llama: LlamaModel (128x64): neck+head, snout, ears, 12x18x10
 *    body, side chests, four 4x14x4 legs. The decor layer (trader blanket or a
 *    carpet) re-draws the model inflated by 0.5 with the equipment sheet. Babies
 *    are the 64x64 baby sheets on their own small geometry, read off the sheet
 *    (body 8x6x13, neck 6x11x4, snout 3x3x3, ears 2x2x2, legs 3x8x3).
 * `view.color` for llamas: bits 0-1 coat, bits 2-6 carpet (dye + 1), bit 7 chest.
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { sheetTexture } from './core';
import type { OwAnim } from './overworldModels';
import { Rig } from './overworldModels2';

const PI = Math.PI;

// ------------------------------------------------------------------ trader

/** WanderingTrader: VillagerModel with the hood-layer hat; the hat rim is empty on this sheet. */
export function wanderingTraderAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/wandering_trader/wandering_trader', 64, 64);
  const head = r.pivot([0, 0, 0]);
  r.box(head, [0, 0], [-4, -10, -4], [8, 10, 8]);
  r.box(head, [24, 0], [-1, -3, -6], [2, 4, 2]);
  r.box(head, [32, 0], [-4, -10, -4], [8, 10, 8], 0.51);
  const body = r.pivot([0, 0, 0]);
  r.box(body, [16, 20], [-4, 0, -3], [8, 12, 6]);
  r.box(body, [0, 38], [-4, 0, -3], [8, 18, 6], 0.5);
  const legR = r.pivot([-2, 12, 0]), legL = r.pivot([2, 12, 0]);
  r.box(legR, [0, 22], [-2, 0, -2], [4, 12, 4]);
  r.box(legL, [0, 22], [-2, 0, -2], [4, 12, 4], 0, true);
  const arms = r.pivot([0, 3, -1], r.parts, [-0.75, 0, 0]);
  r.box(arms, [44, 22], [-8, -2, -2], [4, 8, 4]);
  r.box(arms, [44, 22], [4, -2, -2], [4, 8, 4], 0, true);
  r.box(arms, [40, 38], [-4, 2, -2], [8, 4, 4]);
  return r.anim((v, q, headY, headX) => {
    head.rotation.set(headX, headY, 0);
    const s = q.limbSwing * 0.6662;
    legR.rotation.x = Math.cos(s) * 1.4 * q.limbAmt * 0.5;
    legL.rotation.x = Math.cos(s + PI) * 1.4 * q.limbAmt * 0.5;
    // drinking: the folded arms lift the bottle / bucket toward the face
    arms.rotation.x = v.heldItem !== 0 ? -2.0 : -0.75;
  });
}

// ------------------------------------------------------------------- llama

const COATS = ['creamy', 'white', 'brown', 'gray'] as const;
/** Dye order of the carpet sheets (sheep colour index order). */
const CARPETS = ['white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink', 'gray', 'light_gray', 'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black'] as const;

/** One pass of LlamaModel (adult and baby geometry) over `sheet`; `g` inflates every box. */
function llamaRig(env: EnvUniforms, sheet: string, tw: number, g: number, chests: boolean, adultBoxes: boolean) {
  const r = new Rig(env, sheet, tw, 64);
  const adult = r.pivot([0, 0, 0]), baby = r.pivot([0, 0, 0]);
  // --- adult
  const head = r.pivot([0, 7, -6], adult);
  const body = r.pivot([0, 5, 2], adult, [PI / 2, 0, 0]);
  const chestR = r.pivot([-8.5, 3, 3], adult, [0, PI / 2, 0]), chestL = r.pivot([5.5, 3, 3], adult, [0, PI / 2, 0]);
  const hr = r.pivot([-3.5, 10, 6], adult), hl = r.pivot([3.5, 10, 6], adult);
  const fr = r.pivot([-3.5, 10, -5], adult), fl = r.pivot([3.5, 10, -5], adult);
  if (adultBoxes) {
    r.box(head, [0, 0], [-2, -14, -10], [4, 4, 9], g);
    r.box(head, [0, 14], [-4, -16, -6], [8, 18, 6], g);
    r.box(head, [17, 0], [-4, -19, -4], [3, 3, 2], g);
    r.box(head, [17, 0], [1, -19, -4], [3, 3, 2], g);
    r.box(body, [29, 0], [-6, -10, -7], [12, 18, 10], g);
    if (chests) {
      r.box(chestR, [45, 28], [-3, 0, 0], [8, 8, 3], g);
      r.box(chestL, [45, 41], [-3, 0, 0], [8, 8, 3], g);
    }
    for (const l of [hr, hl, fr, fl]) r.box(l, [29, 29], [-2, 0, -2], [4, 14, 4], g);
  }
  // --- baby (sheet 64x64; adult sheets are 128 wide, so only the baby layout is built for tw = 64)
  const bhead = r.pivot([0, 13, -5], baby);
  const bbody = r.pivot([0, 0, 0], baby);
  const bl = [r.pivot([-2.5, 16, 5.5], baby), r.pivot([2.5, 16, 5.5], baby), r.pivot([-2.5, 16, -3.5], baby), r.pivot([2.5, 16, -3.5], baby)];
  return { r, adult, baby, head, body, chestR, chestL, legs: [hr, hl, fr, fl], bhead, bbody, bl };
}

/** Add the baby boxes (64x64 sheet layout) to a rig built with `llamaRig`. */
function babyBoxes(b: ReturnType<typeof llamaRig>, g: number): void {
  const { r, bhead, bbody, bl } = b;
  r.box(bhead, [0, 0], [-3, -9, -2], [6, 11, 4], g);
  r.box(bhead, [0, 15], [-1.5, -8, -5], [3, 3, 3], g);
  r.box(bhead, [20, 0], [1, -11, -1], [2, 2, 2], g);
  r.box(bhead, [20, 4], [-3, -11, -1], [2, 2, 2], g);
  r.box(bbody, [0, 15], [-4, 10, -5], [8, 6, 13], g);
  r.box(bl[0], [0, 34], [-1.5, 0, -1.5], [3, 8, 3], g);
  r.box(bl[1], [12, 34], [-1.5, 0, -1.5], [3, 8, 3], g);
  r.box(bl[2], [0, 45], [-1.5, 0, -1.5], [3, 8, 3], g);
  r.box(bl[3], [12, 45], [-1.5, 0, -1.5], [3, 8, 3], g);
}

export function llamaAnim(env: EnvUniforms, trader: boolean): OwAnim {
  // adult and baby sheets differ in size, so each gets its own rig (and material); only one is shown
  const A = llamaRig(env, 'entity/llama/llama_creamy', 128, 0, true, true);
  const Ad = llamaRig(env, 'entity/equipment/llama_body/trader_llama', 128, 0.5, false, true);
  const B = llamaRig(env, 'entity/llama/llama_creamy_baby', 64, 0, false, false);
  const Bd = llamaRig(env, 'entity/equipment/llama_body/trader_llama_baby', 64, 0.25, false, false);
  babyBoxes(B, 0); babyBoxes(Bd, 0.25);
  Ad.r.mat.transparent = true; Ad.r.mat.depthWrite = false;
  Bd.r.mat.transparent = true; Bd.r.mat.depthWrite = false;
  const parts = new THREE.Group();
  parts.add(A.r.parts, Ad.r.parts, B.r.parts, Bd.r.parts);
  const all = [A, Ad, B, Bd];
  let skin = '', decor = '';
  return {
    parts, mats: all.flatMap((l) => l.r.mats),
    animate(v, q, headY, headX) {
      const baby = v.baby === true;
      const coat = COATS[v.color & 3];
      const want = `${coat}${baby ? '_baby' : ''}`;
      if (want !== skin) {
        skin = want;
        (baby ? B : A).r.mat.uniforms.uMap.value = sheetTexture(`entity/llama/llama_${want}`);
      }
      const carpet = (v.color >> 2) & 31;
      const dwant = trader ? 'trader_llama' : carpet > 0 ? CARPETS[carpet - 1] ?? '' : '';
      if (dwant !== decor) {
        decor = dwant;
        if (dwant) {
          Ad.r.mat.uniforms.uMap.value = sheetTexture(`entity/equipment/llama_body/${dwant}`);
          Bd.r.mat.uniforms.uMap.value = sheetTexture(`entity/equipment/llama_body/${dwant}${trader ? '_baby' : ''}`);
        }
      }
      A.r.parts.visible = !baby; Ad.r.parts.visible = !baby && dwant !== '';
      B.r.parts.visible = baby; Bd.r.parts.visible = baby && dwant !== '';
      const chest = (v.color & 128) !== 0;
      const s = q.limbSwing * 0.6662, a = q.limbAmt;
      const swing = [Math.cos(s) * 1.4 * a, Math.cos(s + PI) * 1.4 * a, Math.cos(s + PI) * 1.4 * a, Math.cos(s) * 1.4 * a];
      for (const l of all) {
        l.head.rotation.set(headX, headY, 0);
        l.bhead.rotation.set(headX, headY, 0);
        for (let i = 0; i < 4; i++) { l.legs[i].rotation.x = swing[i]; l.bl[i].rotation.x = swing[i]; }
        l.chestR.visible = l.chestL.visible = chest;
      }
    },
    dispose() { for (const l of all) for (const m of l.r.mats) m.dispose(); },
  };
}
